import React from "react";

type Subscriber<T> = React.Dispatch<React.SetStateAction<T>>;
type StorageBus = Record<string, Record<string, Subscriber<unknown>>>;

const lsBus: StorageBus = {};
const ssBus: StorageBus = {};

const safeParse = <T,>(rawValue: string | null, fallback: T): T => {
  if (rawValue === null) {
    return fallback;
  }

  try {
    return JSON.parse(rawValue) as T;
  } catch {
    return fallback;
  }
};

const subscribe = <T,>(
  bus: StorageBus,
  key: string,
  componentId: string,
  setValue: Subscriber<T>
): (() => void) => {
  const keyBucket = bus[key] ?? {};
  bus[key] = keyBucket;
  keyBucket[componentId] = setValue as Subscriber<unknown>;

  return () => {
    const currentBucket = bus[key];
    if (!currentBucket) {
      return;
    }

    delete currentBucket[componentId];
    if (Object.keys(currentBucket).length === 0) {
      delete bus[key];
    }
  };
};

const notifyBus = <T,>(bus: StorageBus, key: string, newValue: T): void => {
  const subscribers = bus[key];
  if (!subscribers) {
    return;
  }

  Object.values(subscribers).forEach((subscriber) => {
    (subscriber as Subscriber<T>)(newValue);
  });
};

export const useLocalStorage = <T,>(
  key: string,
  initialValue?: T
): [T, (newValue?: T) => void] => {
  const fallback = (initialValue ?? null) as T;
  const [value, setValue] = React.useState<T>(() =>
    safeParse<T>(localStorage.getItem(key), fallback)
  );
  const componentId = React.useMemo(() => Math.random().toString(), []);

  React.useEffect(() => {
    return subscribe(lsBus, key, componentId, setValue);
  }, [key, componentId]);

  const setStoredValue = React.useCallback(
    (newValue?: T) => {
      const valueToStore = (newValue ?? null) as T;
      localStorage.setItem(key, JSON.stringify(valueToStore));
      notifyBus(lsBus, key, valueToStore);
    },
    [key]
  );

  return [value, setStoredValue];
};

export const useSessionStorage = <T,>(
  key: string,
  initialValue?: T
): [T, (newValue?: T) => void] => {
  const fallback = (initialValue ?? null) as T;
  const [value, setValue] = React.useState<T>(() =>
    safeParse<T>(sessionStorage.getItem(key), fallback)
  );
  const componentId = React.useMemo(() => Math.random().toString(), []);

  React.useEffect(() => {
    return subscribe(ssBus, key, componentId, setValue);
  }, [key, componentId]);

  const setStoredValue = React.useCallback(
    (newValue?: T) => {
      const valueToStore = (newValue ?? null) as T;
      sessionStorage.setItem(key, JSON.stringify(valueToStore));
      notifyBus(ssBus, key, valueToStore);
    },
    [key]
  );

  return [value, setStoredValue];
};

export const getSessionValue = <T,>(key: string, initialValue?: T): T | null => {
  return safeParse<T | null>(sessionStorage.getItem(key), (initialValue ?? null) as T | null);
};

export const removeSessionValue = (key: string): void => {
  sessionStorage.removeItem(key);
  notifyBus(ssBus, key, undefined);
};
